import Sprite from '../ui/Sprite.js';
import PokemonMenu from '../ui/PokemonMenu.js';
import OptionsBox from '../ui/OptionsBox.js';
import PokemonSummary from '../ui/PokemonSummary.js';
import NewMoveMenu from '../ui/NewMoveMenu.js';
import BagMenu from '../ui/BagMenu.js';
import FoePokemonBattleInfoBox from '../ui/FoePokemonBattleInfoBox.js';
import PlayerPokemonBattleInfoBox from '../ui/PlayerPokemonBattleInfoBox.js';
import MoveMenu from '../ui/MoveMenu.js';
import BattleMenu from '../ui/BattleMenu.js';
import StatIncreaseBox from '../ui/StatIncreaseBox.js';
import WhiteFlash from '../ui/WhiteFlash.js';
import AddedToPokedexSummary from '../ui/AddedToPokedexSummary.js';
import DarkScreen from '../ui/DarkScreen.js';
import PixelText from '../ui/PixelText.js';
import { FoePartySummary, PlayerPartySummary } from '../ui/party-summary/TrainerBattlePartySummary.js';
import { getOSM, getBattleStateMachine } from '../OverworldAnimation.js';

import { imageStore, spriteStore } from '../images.js';
import { c } from '../setup.js';
import { keyEnterPressInstant } from '../BattleAnimation.js';

export default class BattleGUI {
    constructor() {
        this.cursor = 0;
        this.text = new PixelText({
            text: '',
            color: PixelText.whiteTextColor,
            shadowColor: PixelText.blackShadowColor,
            useCursor: true,
            canScroll: true,
            position: {
                x: 12,
                y: 124,
            },
            maxWidth: 200,
            includeBounds: true,
            fontType: 'large',
        });

        const foeTrainerFront = spriteStore.store.foeTrainerFront;
        if (this.foeTrainer) {
            foeTrainerFront.image = imageStore.get(`img/npc/${this.foeTrainer.type}/front.png`);
        }
        const battleBackgroundFoe = spriteStore.store.battleBackgroundFoe;
        const battleBackgroundPlayer = spriteStore.store.battleBackgroundPlayer;
        const battleBackgroundNoStage = spriteStore.store.battleBackgroundNoStage;
        const playerBack =
            getOSM().player.gender === 'boy'
                ? spriteStore.store.playerBackBoy
                : spriteStore.store.playerBackGirl;
        const battleTextBox = spriteStore.store.battleTextBox;
        const statIncreaseBox = spriteStore.store.statIncreaseBox;

        this.drawables = {
            background: battleBackgroundNoStage,
            foeBackground: battleBackgroundFoe,
            playerBackground: battleBackgroundPlayer,
            player: playerBack,
            foeTrainer: foeTrainerFront,
            textBox: battleTextBox,
            statBox: statIncreaseBox,
            images: {},
        };

        this.darkScreen = new DarkScreen({
            visible: true,
            alpha: 1,
        });

        this.showHpAsPercentage = false;
    }

    updateBattleBackground() {}

    darken() {
        if (this.darkScreen.alpha < 1) {
            this.darkScreen.alpha += 0.1;
        } else {
            this.darkScreen.alpha = 1;
        }
        return this.darkScreen.alpha === 1;
    }

    undarken() {
        if (this.darkScreen.alpha > 0) {
            this.darkScreen.alpha -= 0.1;
        } else {
            this.darkScreen.alpha = 0;
        }
        return this.darkScreen.alpha === 0;
    }

    setup() {
        const bsm = getBattleStateMachine();
        if (!bsm) return;
        // Clear canvas
        c.clearRect(0, 0, 240, 160);

        this.drawables.foeInfo = new FoePokemonBattleInfoBox({
            pokemon: bsm.foePokemon,
            sprites: {
                battleSceneFoeInfo: spriteStore.store.battleSceneFoeInfo,
                battleSceneFoeInfoNoHPLabel: spriteStore.store.battleSceneFoeInfoNoHPLabel,
            },
            position: spriteStore.store.battleSceneFoeInfo.position,
            caught: bsm.player.pokedex.isInPokedex(bsm.foePokemon) && bsm.foeTrainer === null,
        });
        this.drawables.playerInfo = new PlayerPokemonBattleInfoBox({
            pokemon: bsm.playerPokemon,
            sprites: { battleScenePlayerInfo: spriteStore.store.battleScenePlayerInfo },
            position: spriteStore.store.battleScenePlayerInfo.position,
        });
        this.drawables.foePokemon = bsm.foePokemon.frontSprite;
        if (this.drawables.foePokemon) {
            this.drawables.foePokemon.position.x = -32 - 64;
            this.drawables.foePokemon.position.y = 16;
            this.drawables.foePokemon.visible = true;
        }
        this.drawables.playerPokemon = bsm.playerPokemon.backSprite;

        this.drawables.playerPokemon.scale = 0;
        this.drawables.playerPokemon.verticalCrop = 0;
        this.drawables.playerPokemon.verticalOffset = 0;

        if (bsm.foeTrainer !== null) {
            this.drawables.foeTrainer.image = imageStore.get(`img/npc/${bsm.foeTrainer.type}/front.png`);

            this.drawables.foeTrainer.visible = true;
        } else {
            this.drawables.foeTrainer.visible = false;
        }

        this.drawables.moveMenu = new MoveMenu({
            pokemon: bsm.playerPokemon,
            visible: false,
        });
        this.drawables.battleMenu = new BattleMenu({
            pokemon: bsm.playerPokemon,
            visible: false,
        });
        this.drawables.statBox = new StatIncreaseBox({
            pokemon: bsm.playerPokemon,
            visible: false,
            position: {
                x: 160,
                y: 56,
            },
        });
        this.drawables.pokemonMenu = new PokemonMenu({
            partyPokemon: bsm.player.party,
            currentOption: Math.round(bsm.pokemonOption),
            visible: false,
            position: { x: 0, y: 0 },
        });

        this.drawables.pokemonSummary = new PokemonSummary({
            pokemon: bsm.playerPokemon,
            currentPage: 0, // 0: Summary, 1: Stats, 2: Moves, 3: Moves Detail
            visible: false,
            position: { x: 0, y: 0 },
        });

        this.drawables.overworldTextBoxImage = imageStore.get('img/overworld_text_box.png');
        this.drawables.overworldTextBox = new Sprite({
            position: { x: 0, y: 112 },
            image: this.drawables.overworldTextBoxImage,
            frames: { val: 0, max: 1 },
            moving: false,
            visible: false,
        });

        this.drawables.trainerPokeball = new Sprite({
            position: this.drawables.player.position,
            image: imageStore.get('img/player_back_pokeball.png'),
            frames: { val: 0, max: 5 },
            moving: false,
        });

        this.drawables.images = {
            pokeballOpeningImage: imageStore.get('img/pokeball_opening.png'),
            pokeballShakeImage: imageStore.get('img/pokeball_shake.png'),
            pokeballClosingImage: imageStore.get('img/pokeball_closing.png'),
            thrownPokeballImage: imageStore.get('img/pokeball.png'),
        };

        this.drawables.thrownPokeball = new Sprite({
            position: { x: this.drawables.player.position.x + 64, y: this.drawables.player.position.y },
            image: imageStore.get('img/pokeball.png'),
            frames: { val: 0, max: 8, stopAtEnd: false },
            moving: false,
            hold: 4,
            visible: false,
            yVelocity: 0,
            gravity: 0,
        });

        this.drawables.yesOrNoMenu = new OptionsBox({
            position: { x: 176, y: 64 },
            visible: false,
            options: ['Yes', 'No'],
            selectedOption: 0,
        });

        this.drawables.pokemonSummary = new PokemonSummary({
            pokemon: bsm.playerPokemon,
            currentPage: 0,
            visible: false,
            position: { x: 0, y: 0 },
        });

        this.drawables.bagMenu = new BagMenu({
            visible: false,
            position: { x: 0, y: 0 },
            items: bsm.player.items,
            currentPage: 0,
            stateMachine: bsm,
        });

        this.drawables.whiteFlash = new WhiteFlash();
        this.drawables.addedToPokedexSummary = new AddedToPokedexSummary({
            pokemon: bsm.foePokemon,
        });
        this.drawables.addedToPokedexSummary.visible = false;
        this.drawables.newMoveMenu = new NewMoveMenu({
            visible: false,
            position: { x: 0, y: 0 },
            pokemon: bsm.playerPokemon,
            newMove: null,
        });
        this.drawables.playerPartySummary = new PlayerPartySummary(bsm.player.party);
        this.drawables.foePartySummary = new FoePartySummary([]);

        this.darkScreen.alpha = 1;
    }

    update() {
        const bsm = getBattleStateMachine();
        if (!bsm) return;
        bsm.playerPokemon.currentSprite = bsm.playerPokemon.backSprite;
        bsm.foePokemon.currentSprite = bsm.foePokemon.frontSprite;

        this.text.cursor = bsm.cursor;
        this.text.text = bsm.battleText;

        this.drawables.background.draw();
        this.drawables.playerBackground.draw();
        this.drawables.foeBackground.draw();
        this.drawables.whiteFlash.draw();

        this.drawables.foeTrainer.draw();
        this.drawables.foePokemon = bsm.foePokemon.frontSprite;
        if (this.drawables.foePokemon) {
            this.drawables.foePokemon.spriteIndex = bsm.foePokemon.id - 1;
            this.drawables.foePokemon.draw();
        }
        this.drawables.foePokemonStatIncreaseAnimation = bsm.foePokemon.statIncreaseAnimation;
        this.drawables.foePokemonStatIncreaseAnimation.position = bsm.foePokemon.currentSprite.position;
        this.drawables.foePokemonStatIncreaseAnimation.draw();
        this.drawables.foePokemonStatDecreaseAnimation = bsm.foePokemon.statDecreaseAnimation;
        this.drawables.foePokemonStatDecreaseAnimation.position = bsm.foePokemon.currentSprite.position;
        this.drawables.foePokemonStatDecreaseAnimation.draw();
        this.drawables.trainerPokeball.position = this.drawables.player.position;
        this.drawables.trainerPokeball.draw();
        this.drawables.player.draw();
        this.drawables.playerPokemon = bsm.playerPokemon.backSprite;
        this.drawables.playerPokemon.spriteIndex = bsm.playerPokemon.id - 1;
        this.drawables.playerPokemon.draw();

        bsm.playerPokemon.statChangeAnimations.attack.draw({
            mask: this.drawables.playerPokemon,
        });
        bsm.playerPokemon.statChangeAnimations.defense.draw({
            mask: this.drawables.playerPokemon,
        });
        bsm.playerPokemon.statChangeAnimations.specialAttack.draw({
            mask: this.drawables.playerPokemon,
        });
        bsm.playerPokemon.statChangeAnimations.specialDefense.draw({
            mask: this.drawables.playerPokemon,
        });
        bsm.playerPokemon.statChangeAnimations.evasiveness.draw({
            mask: this.drawables.playerPokemon,
        });
        bsm.playerPokemon.statChangeAnimations.accuracy.draw({
            mask: this.drawables.playerPokemon,
        });
        bsm.playerPokemon.statChangeAnimations.speed.draw({
            mask: this.drawables.playerPokemon,
        });
        bsm.playerPokemon.statChangeAnimations.mix.draw({
            mask: this.drawables.playerPokemon,
        });

        bsm.foePokemon.statChangeAnimations.attack.draw({
            mask: this.drawables.foePokemon,
        });
        bsm.foePokemon.statChangeAnimations.defense.draw({
            mask: this.drawables.foePokemon,
        });
        bsm.foePokemon.statChangeAnimations.specialAttack.draw({
            mask: this.drawables.foePokemon,
        });
        bsm.foePokemon.statChangeAnimations.specialDefense.draw({
            mask: this.drawables.foePokemon,
        });
        bsm.foePokemon.statChangeAnimations.evasiveness.draw({
            mask: this.drawables.foePokemon,
        });
        bsm.foePokemon.statChangeAnimations.accuracy.draw({
            mask: this.drawables.foePokemon,
        });
        bsm.foePokemon.statChangeAnimations.speed.draw({
            mask: this.drawables.foePokemon,
        });
        bsm.foePokemon.statChangeAnimations.mix.draw({ mask: this.drawables.foePokemon });
        this.drawables.thrownPokeball.draw();
        this.drawables.textBox.draw();
        this.drawables.battleMenu.pokemon = bsm.playerPokemon;
        this.drawables.battleMenu.option = bsm.battleOption;
        this.drawables.battleMenu.draw();
        this.drawables.moveMenu.pokemon = bsm.playerPokemon;
        this.drawables.moveMenu.option = bsm.moveOption;
        this.drawables.moveMenu.resolvedMoves = [
            bsm.getPlayerMoveForSlot(0),
            bsm.getPlayerMoveForSlot(1),
            bsm.getPlayerMoveForSlot(2),
            bsm.getPlayerMoveForSlot(3),
        ];
        this.drawables.moveMenu.draw();
        if (keyEnterPressInstant) {
            this.showHpAsPercentage = !this.showHpAsPercentage;
        }
        this.drawables.foeInfo.pokemon = bsm.foePokemon;
        this.drawables.foeInfo.showHpAsPercentage = this.showHpAsPercentage;
        this.drawables.foeInfo.draw();
        this.drawables.playerInfo.pokemon = bsm.playerPokemon;
        this.drawables.playerInfo.showHpAsPercentage = this.showHpAsPercentage;
        this.drawables.playerInfo.draw();
        this.drawables.playerPartySummary.draw();
        this.drawables.foePartySummary.draw();
        this.drawables.statBox.pokemon = bsm.playerPokemon;
        this.drawables.statBox.draw();
        this.drawables.bagMenu.currentPage = bsm.bagPage;
        this.drawables.bagMenu.currentItem = bsm.bagItem;
        this.drawables.bagMenu.items = bsm.availableItems;
        this.drawables.bagMenu.sprites.bagOptionsBox.selectedOption = bsm.bagDetailOption;
        // this.drawables.bagMenu.draw();
        this.drawables.pokemonMenu.helpText.text = bsm.pokemonMenuText;
        this.drawables.pokemonMenu.currentOption = bsm.pokemonOption;
        this.drawables.pokemonMenu.sprites.pokemonOptionsBox.selectedOption = bsm.pokemonDetailOption;
        this.drawables.pokemonMenu.sprites.moveOptionsBox.selectedOption = bsm.moveItemOption;
        this.drawables.pokemonMenu.draw();
        this.drawables.pokemonSummary.currentPage = bsm.summaryOption;
        this.drawables.pokemonSummary.pokemon = bsm.player.party[bsm.pokemonOption];
        this.drawables.pokemonSummary.showMoveSummary = bsm.showMoveSummary;
        this.drawables.pokemonSummary.selectedMove = bsm.summaryMoveDetailOption;
        this.drawables.pokemonSummary.draw();
        this.drawables.bagMenu.currentPage = bsm.bagPage;
        this.drawables.bagMenu.currentItem = bsm.bagItem;
        // TODO: Available items should be calculated elsewhere
        this.drawables.bagMenu.items = [bsm.player.healingItemsList(), bsm.player.pokeballsList()];
        this.drawables.bagMenu.draw();
        this.drawables.newMoveMenu.selectedMove = bsm.summaryMoveDetailOption;
        this.drawables.newMoveMenu.draw();
        this.drawables.addedToPokedexSummary.draw();
        this.drawables.yesOrNoMenu.selectedOption = bsm.yesOrNoOption;
        this.drawables.yesOrNoMenu.draw();
        this.drawables.overworldTextBox.draw();
        this.text.draw();
        this.darkScreen.draw();
    }
}
